import React from 'react';
import { TurnColorWord, STANDARD_COLORS } from '@/lib/turn-color';
import { CELEBRATION_WHITELIST } from './constants';

export type VisualState =
  | { type: 'idle' }
  | { type: 'consonant'; letter: string }
  | { type: 'color'; color: string }
  | { type: 'letter_on_color'; letter: string; color: string }
  | { type: 'word'; word: string; turnCode: string }
  | { type: 'celebration'; message: string };

interface Props {
  state: VisualState;
}

export const TutorDisplay: React.FC<Props> = ({ state }) => {
  const renderContent = () => {
    switch (state.type) {
      case 'idle':
        return (
          <div className="flex items-center justify-center h-64 text-[#D4D4D4] text-lg" aria-label="Waiting to start">
            Ready to learn!
          </div>
        );
      case 'consonant':
        return (
          <div className="flex items-center justify-center h-64" aria-label={`Showing letter ${state.letter}`}>
            <span className="text-9xl font-bold text-[#1A1A1A] font-mono">{state.letter}</span>
          </div>
        );
      case 'color': {
        const hex = STANDARD_COLORS.find(c => c.color_name === state.color)?.hex_code || '#555';
        return (
          <div className="flex items-center justify-center h-64" aria-label={`Showing color ${state.color}`}>
            <div className="w-48 h-48 rounded-2xl" style={{ backgroundColor: hex }} />
          </div>
        );
      }
      case 'letter_on_color': {
        const hex = STANDARD_COLORS.find(c => c.color_name === state.color)?.hex_code || '#555';
        return (
          <div className="flex items-center justify-center h-64" aria-label={`Showing letter ${state.letter} on ${state.color}`}>
            <div
              className="w-48 h-48 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: hex }}
            >
              <span className="text-8xl font-bold text-[#1A1A1A] font-mono">{state.letter}</span>
            </div>
          </div>
        );
      }
      case 'word':
        return (
          <div className="flex items-center justify-center h-64" aria-label={`Showing word ${state.word}`}>
            <div className="text-7xl">
              <TurnColorWord word={state.word} turnCode={state.turnCode} />
            </div>
          </div>
        );
      case 'celebration': {
        const safeMessage = CELEBRATION_WHITELIST.includes(state.message)
          ? state.message
          : 'Great job!';
        return (
          <div className="flex flex-col items-center justify-center h-64 gap-4" aria-label={`Celebration: ${safeMessage}`}>
            <span className="text-8xl">&#11088;</span>
            <span className="text-4xl font-bold text-[#1A1A1A]">{safeMessage}</span>
          </div>
        );
      }
      default:
        return (
          <div className="flex items-center justify-center h-64 text-[#D4D4D4] text-lg" aria-label="Waiting to start">
            Ready to learn!
          </div>
        );
    }
  };

  return (
    <div role="status" aria-live="polite">
      {renderContent()}
    </div>
  );
};
